import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Dropdown, Input, Popover } from 'antd';

import { CHECK, CHEVRON_DOWN, KEBAB, PIN, SEARCH_GLASS } from '../icons';
import { FILTER_SCOPE } from '../../hooks/useSavedFilters';
import { normalizeId } from '../../utils/ids';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { describeSavedFilterChanges } from '../../utils/savedFilterState';
import { buildActionMenu } from '../common/actionMenu';
import GroupHeader from '../common/GroupHeader';
import { DataState, Skeleton } from '../loading';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { PIN_KIND } from '../../services/favorites';

export const COLLECTION_PIN_TITLE = 'Pinned for everyone by a collection manager';
export const ALL_ITEMS = 'All items';

/** The rows a search for `needle` leaves: a case-insensitive match on the name. */
export function filterSavedFilters(filters = [], needle = '') {
	const term = `${needle || ''}`.trim().toLowerCase();
	if (!term) return filters;
	return filters.filter(filter => `${filter?.name || ''}`.toLowerCase().includes(term));
}

/**
 * THE SAVED FILTER MENU (KPR-1642, KPE-051 rulings i02, i11, i12): the first
 * control of the items page's first toolbar row. It replaced the saved
 * filters' chips.
 *
 * The trigger always names what you are looking at: the applied saved
 * filter, with a dot when the screen has changed since ("In flight •"), or
 * **All items**. It opens a 320px menu (a bottom drawer on a phone): a search
 * box that narrows by name, an All items row, then **Pinned for everyone**
 * (the collection's filters, each with a pin) and **Mine**. Each row's ⋯ holds
 * Rename · Pin to Home · Pin for everyone or Unpin (managers) · Delete, by the
 * permissions the API keeps: a person's own filters are theirs to change, the
 * collection's are its managers' (the 404/403 line). Nobody else's own
 * filters are ever listed.
 *
 * Keys: Enter or a click opens it with the cursor in the search box; ↓ and ↑
 * walk the rows; Enter on a row (or in the box, the first row shown) applies
 * it; Escape closes and puts focus back on the trigger.
 *
 * It keeps no saved-filter state of its own: the list, the applied filter and
 * what changed are hooks/useSavedFilterControls'.
 */
export default function SavedFilterPicker({
	collectionId,
	filters = [],
	readState = 'ready',
	loaded = true,
	onRetry,
	applied = null,
	changes = [],
	pendingName = false,
	canManage = false,
	isMobile = false,
	onApply,
	onAll,
	onRename,
	onDelete,
	onPromote,
	onDemote,
}) {
	const favorites = useOptionalFavorites();
	const [open, setOpen] = useState(false);
	const [needle, setNeedle] = useState('');
	const triggerRef = useRef(null);
	const panelRef = useRef(null);
	const appliedId = normalizeId(applied?._id);
	const changed = Boolean(applied && changes.length);

	const close = useCallback(({ restoreFocus = false } = {}) => {
		setOpen(false);
		setNeedle('');
		if (restoreFocus) setTimeout(() => triggerRef.current?.focus?.(), 0);
	}, []);

	// Opening puts the cursor in the search box. A touch screen is left alone:
	// its keyboard would cover the list it is meant to search.
	const focusSearch = useCallback(() => {
		const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches;
		if (coarse) return;
		panelRef.current?.querySelector?.('.saved-filter-menu__search input')?.focus?.();
	}, []);

	const rows = () => [...(panelRef.current?.querySelectorAll?.('[data-saved-filter-row]') || [])];
	const onPanelKeyDown = (event) => {
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			close({ restoreFocus: true });
			return;
		}
		if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
		const list = rows();
		if (!list.length) return;
		event.preventDefault();
		const at = list.indexOf(document.activeElement);
		const next = event.key === 'ArrowDown'
			? (at < 0 ? 0 : Math.min(list.length - 1, at + 1))
			: (at <= 0 ? -1 : at - 1);
		if (next < 0) focusSearch();
		else list[next].focus();
	};

	const choose = (filter) => {
		close({ restoreFocus: true });
		if (filter) onApply(filter);
		else onAll();
	};

	const shown = useMemo(() => filterSavedFilters(filters, needle), [filters, needle]);
	const pinned = shown.filter(filter => filter?.scope === FILTER_SCOPE.COLLECTION);
	const mine = shown.filter(filter => filter?.scope !== FILTER_SCOPE.COLLECTION);
	const searching = Boolean(needle.trim());

	const rowMenu = (filter) => {
		const id = normalizeId(filter?._id);
		const isCollectionTier = filter?.scope === FILTER_SCOPE.COLLECTION;
		// A collection filter is its managers' to change; a person's own is
		// always theirs (the API never lists anybody else's).
		const mayEdit = !isCollectionTier || canManage;
		// A filter still being saved (its id a placeholder) has nothing to pin.
		const homeId = filter?.pending ? null : id;
		const onHome = Boolean(homeId && favorites?.isPinned?.(PIN_KIND.SAVED_FILTER, homeId, collectionId));
		const leaving = (fn) => () => { close(); fn(filter); };
		return buildActionMenu([
			{ key: 'rename', group: 'record', glyph: 'edit', label: 'Rename', hidden: !mayEdit || filter?.pending, onClick: leaving(onRename) },
			// A shortcut of the person's own: offered to everyone who can see it.
			{
				key: 'pin-home',
				group: 'record',
				glyph: 'pin',
				label: onHome ? 'Unpin from Home' : 'Pin to Home',
				hidden: !homeId || !favorites?.togglePin,
				onClick: () => favorites.togglePin(PIN_KIND.SAVED_FILTER, { targetId: homeId, collectionId: normalizeId(collectionId) }),
			},
			{ key: 'demote', group: 'record', glyph: 'collection', label: 'Unpin — return to your own list', hidden: !(canManage && isCollectionTier), onClick: () => onDemote(filter) },
			{ key: 'promote', group: 'record', glyph: 'collection', label: 'Pin for everyone', hidden: !(canManage && !isCollectionTier) || filter?.pending, onClick: () => onPromote(filter) },
			{ key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete', hidden: !mayEdit || filter?.pending, onClick: leaving(onDelete) },
		]);
	};

	const renderRow = (filter) => {
		const id = normalizeId(filter?._id);
		const name = filter?.name || '(untitled filter)';
		const isCollectionTier = filter?.scope === FILTER_SCOPE.COLLECTION;
		const isApplied = id === appliedId;
		const menu = rowMenu(filter);
		return (
			<li key={id} className={`saved-filter-menu__row${isApplied ? ' saved-filter-menu__row--applied' : ''}`}>
				<button
					type="button"
					className="saved-filter-menu__apply"
					data-saved-filter-row
					aria-current={isApplied ? 'true' : undefined}
					title={isCollectionTier ? `${name} · ${COLLECTION_PIN_TITLE}` : name}
					onClick={() => choose(filter)}
				>
					{isCollectionTier ? <PIN className="saved-filter-menu__pin" aria-label="Pinned for everyone" role="img" /> : null}
					<span className="saved-filter-menu__row-name">{name}</span>
					{isApplied ? <CHECK className="saved-filter-menu__check" aria-hidden="true" /> : null}
				</button>
				{menu ? (
					<Dropdown trigger={['click']} menu={menu} placement="bottomRight">
						<Button
							type="text"
							className="saved-filter-menu__more"
							icon={<KEBAB aria-hidden="true" />}
							aria-label={`More for ${name}`}
						/>
					</Dropdown>
				) : null}
			</li>
		);
	};

	const pending = readState === 'loading-initial' || readState === 'error';
	const section = (label, list) => (list.length ? (
		<div className="saved-filter-menu__section" role="group" aria-label={label}>
			<GroupHeader label={label} count={list.length} />
			<ul className="saved-filter-menu__list">{list.map(renderRow)}</ul>
		</div>
	) : null);

	const panel = (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div
			ref={panelRef}
			className={`saved-filter-menu${isMobile ? ' saved-filter-menu--drawer' : ''}`}
			role="group"
			aria-label="Saved filters"
			onKeyDown={onPanelKeyDown}
			data-testid="saved-filter-menu"
		>
			<div className="saved-filter-menu__search">
				<Input
					{...NO_TEXT_ASSIST}
					allowClear
					prefix={<SEARCH_GLASS aria-hidden="true" />}
					placeholder="Search saved filters"
					aria-label="Search saved filters"
					value={needle}
					onChange={event => setNeedle(event.target.value)}
					onPressEnter={() => { if (shown[0]) choose(shown[0]); }}
				/>
			</div>
			<ul className="saved-filter-menu__list saved-filter-menu__list--all">
				<li className={`saved-filter-menu__row${applied ? '' : ' saved-filter-menu__row--applied'}`}>
					<button
						type="button"
						className="saved-filter-menu__apply"
						data-saved-filter-row
						aria-current={applied ? undefined : 'true'}
						onClick={() => choose(null)}
					>
						<span className="saved-filter-menu__row-name">{ALL_ITEMS}</span>
						{applied ? null : <CHECK className="saved-filter-menu__check" aria-hidden="true" />}
					</button>
				</li>
			</ul>
			{pending ? (
				<DataState
					className="saved-filter-menu__state"
					state={readState}
					hasData={loaded}
					skeleton={<Skeleton variant="line" count={2} />}
					onRetry={onRetry}
					label="saved filters"
				/>
			) : (
				<>
					{section('Pinned for everyone', pinned)}
					{section('Mine', mine)}
					{!shown.length ? (
						<p className="saved-filter-menu__empty empty-line">
							{searching ? 'No saved filter matches that name' : 'No saved filters yet. Build a filter, then choose Save filter.'}
						</p>
					) : null}
				</>
			)}
		</div>
	);

	const name = applied ? (applied.name || '(untitled filter)') : (pendingName ? 'Saved filter' : ALL_ITEMS);
	const changedWords = changed ? `Changed since it was saved: ${describeSavedFilterChanges(changes)}.` : null;
	const trigger = (
		<Button
			ref={triggerRef}
			className={`saved-filter-menu__trigger${changed ? ' saved-filter-menu__trigger--changed' : ''}`}
			aria-haspopup="dialog"
			aria-expanded={open}
			aria-label={`Saved filter: ${name}${changed ? ', changed' : ''}`}
			title={changedWords || name}
			onClick={isMobile ? () => setOpen(true) : undefined}
			data-saved-filter-trigger
		>
			<span className="saved-filter-menu__name">{name}</span>
			{changed ? <span className="saved-filter-menu__dot" aria-hidden="true" data-saved-filter-dot /> : null}
			<CHEVRON_DOWN aria-hidden="true" className="saved-filter-menu__caret" />
		</Button>
	);

	if (isMobile) {
		return (
			<>
				{trigger}
				<Drawer
					open={open}
					placement="bottom"
					height="auto"
					title="Saved filter"
					className="saved-filter-menu__drawer"
					keyboard={false}
					onClose={() => close({ restoreFocus: true })}
					afterOpenChange={visible => { if (visible) focusSearch(); }}
					destroyOnHidden
				>
					{panel}
				</Drawer>
			</>
		);
	}

	return (
		<Popover
			open={open}
			onOpenChange={next => (next ? setOpen(true) : close())}
			afterOpenChange={visible => { if (visible) focusSearch(); }}
			trigger="click"
			placement="bottomLeft"
			arrow={false}
			autoAdjustOverflow={{ adjustX: true, adjustY: true, shiftX: true }}
			destroyOnHidden
			content={panel}
			overlayClassName="saved-filter-menu__popover"
		>
			{trigger}
		</Popover>
	);
}
